import React, { useEffect, useState } from 'react';
import { MapPin, Search } from 'lucide-react';

interface LocationSelectorProps {
  onLocationSelect: (location: { lat: number; lng: number }, address: string) => void;
}

type NominatimResult = {
  display_name: string;
  lat: string;
  lon: string;
};

const LocationSelector: React.FC<LocationSelectorProps> = ({ onLocationSelect }) => {
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  
  useEffect(() => {
    if (!searchValue.trim()) {
      setResults([]);
      setSearchError(null);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setIsSearching(true);
      setSearchError(null);
      try {
        const params = new URLSearchParams({
          format: 'json',
          q: searchValue,
          addressdetails: '1',
          limit: '5',
        });
        // const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
        const response = await fetch(`/geocode?${params.toString()}`, {
          headers: {
            'Accept-Language': 'en',
          },
        });
        if (!response.ok) {
          throw new Error(`Nominatim error: ${response.status}`);
        }
        const data = (await response.json()) as NominatimResult[];
        setResults(data);
      } catch (error) {
        console.error('Nominatim search failed:', error);
        setSearchError('Unable to search for the city. Please try again.');
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [searchValue]);

  const handleResultSelect = (result: NominatimResult) => {
    const lat = Number(result.lat);
    const lng = Number(result.lon);
    const isValidLocation = Number.isFinite(lat) && Number.isFinite(lng);
    if (!isValidLocation) {
      setSearchError('Invalid coordinates for this location.');
      return;
    }

    setAddress(result.display_name);
    setCoordinates({ lat, lng });
    setResults([]);
    onLocationSelect({ lat, lng }, result.display_name);
  };

  return (
    <div className="space-y-3">
      <div className="flex">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search for a city"
          className="flex-grow px-4 py-2 bg-gray-700 border border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
        />
        <button
          type="button"
          onClick={() => setSearchValue(searchValue)}
          className="flex items-center justify-center px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-r-md transition-colors"
        >
          <Search className="w-5 h-5" />
        </button>
      </div>

      {isSearching && (
        <p className="text-xs text-gray-400">Searching...</p>
      )}

      {searchError && (
        <p className="text-xs text-red-400">{searchError}</p>
      )}

      {results.length > 0 && (
        <div className="bg-gray-700 rounded-md divide-y divide-gray-600 text-sm text-gray-200">
          {results.map((result) => (
            <button
              key={`${result.lat}-${result.lon}-${result.display_name}`}
              type="button"
              onClick={() => handleResultSelect(result)}
              className="w-full text-left px-4 py-2 hover:bg-gray-600 transition-colors"
            >
              {result.display_name}
            </button>
          ))}
        </div>
      )}

      <div className="bg-gray-700 rounded-md p-4 text-sm text-gray-200">
        <div className="flex items-center mb-2">
          <MapPin className="w-4 h-4 mr-2 text-purple-400" />
          <span>Location preview</span>
        </div>
        {address ? (
          <div className="space-y-1">
            <p className="text-gray-100">{address}</p>
            {coordinates && (
              <p className="text-xs text-gray-300">
                Lat: {coordinates.lat.toFixed(6)} · Lng: {coordinates.lng.toFixed(6)}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400">
            Select a city to view coordinates and address.
          </p>
        )}
      </div>
    </div>
  );
};

export default LocationSelector;
